import { Empty } from 'antd';
import type { EmptyProps } from 'antd';

import styles from './NoDataBoundary.module.css';

/**
 * 데이터 유무에 따라 children 또는 Empty 컴포넌트를 렌더링하는 경계 컴포넌트
 *
 * @description
 * 데이터 배열이 비어있을 때 Empty 상태를 표시하고,
 * 데이터가 있을 때는 children을 렌더링합니다.
 *
 * @example
 * ```tsx
 * <NoDataBoundary data={services}>
 *   <ServiceList services={services} />
 * </NoDataBoundary>
 * ```
 */

export type NoDataBoundaryProps = {
  /** 검사할 데이터 배열 */
  data: unknown[] | null | undefined | unknown;
  /** 데이터가 있을 때 렌더링할 컴포넌트 */
  children: React.ReactNode;
  /** Empty 컴포넌트에 전달할 props (옵션) */
  emptyProps?: Partial<EmptyProps>;
  /** 커스텀 Empty 메시지 */
  emptyDescription?: React.ReactNode;
  /** 커스텀 클래스명 */
  className?: string;
};

export const NoDataBoundary: React.FC<NoDataBoundaryProps> = ({
  data,
  children,
  emptyProps,
  emptyDescription = '데이터가 없습니다',
  className,
}) => {
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <div className={`${styles.emptyContainer} ${className || ''}`}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} {...emptyProps} />
      </div>
    );
  }

  return <>{children}</>;
};
